/*
 * Iconos ilustrados: el segundo idioma gráfico de la plataforma.
 *
 * La plataforma hablaba SEIS idiomas gráficos a la vez —línea de Lucide,
 * vector plano de `iconos-materias/`, caricatura de las mascotas, 3D con
 * brillo de `iconos3d/`, logos de terceros y emoji— y a veces varios en la
 * misma pantalla. Aquí se fija el reparto:
 *
 *   · LÍNEA (Lucide)      → navegar y mandar: menú, botones, chevrons.
 *   · ILUSTRADO (esto)    → nombrar CONTENIDO: materias, unidades, tipos de paso.
 *   · MASCOTA             → la materia en las salas de niños.
 *   · LOGO                → terceros, y nada más.
 *
 * Todos salen de UN set: los 64 render 3D de `public/assets/iconos3d/`.
 *
 * ANTES ESTO ERA UN SET PLANO, y conviene saber por qué cambió. Se eligió plano
 * con dos argumentos, y uno era FALSO: que los packs 3D pesaban hasta 6000×7260
 * px por pieza. Cierto de los packs crudos, pero los de este directorio ya
 * estaban procesados — 160×160 px y 28 KB de media, 1,7 MB los 64. Es diez
 * veces lo que pesaba el set vectorial y sigue siendo nada, y a 160 px sirven
 * de sobra para los tamaños en que se pintan (40 en la unidad, 68 en la
 * tarjeta, 38 en el tipo de paso).
 *
 * El segundo argumento —que lo plano convive mejor con las mascotas— resultó
 * ser cuestión de gusto, y esa decisión es del cliente. Elegido el 3D, se gana
 * de paso vocabulario: este set trae `ia-robot`, `adn`, `sistema-solar` y
 * `escuadra`, cosas que con el plano hubo que resolver con compromisos.
 *
 * Los nombres ya vienen en español, así que la tabla se lee sola.
 */

/** Nombre de fichero (sin extensión) dentro de public/assets/iconos3d/. */
export type IconoIlustrado = string;

export function rutaIcono(nombre: IconoIlustrado): string {
  return `/assets/iconos3d/${nombre}.png`;
}

/* ---------------------------------------------------------------- *
 * 1. Tipo de paso, dentro de la lección
 * ---------------------------------------------------------------- */

/*
 * Cuatro iconos y siempre los mismos. Un nene de 1ro reconoce "ahora me toca a
 * mí" por la forma antes que por la palabra, y solo funciona si la forma no
 * cambia de una lección a otra.
 */
export const ICONO_POR_PASO: Record<string, IconoIlustrado> = {
  teach: "chat",                  // bocadillos: alguien te está explicando
  interactive: "edu-bloques-abc", // bloques de juguete: esto se toca
  practice: "edu-escritura",      // lápiz y pluma: te toca a ti
  summary: "edu-trofeo",          // trofeo: lo lograste
};

/* ---------------------------------------------------------------- *
 * 2. Materia
 * ---------------------------------------------------------------- */

/*
 * Por SLUG de módulo, con las variantes de nivel resueltas por prefijo (ver
 * `iconoDeMateria`): `matematicas`, `matematicas-intermedia` y
 * `matematicas-superior` comparten icono igual que ya comparten acento. Un
 * estudiante que sube de nivel reconoce su materia; cambiarle el dibujo por el
 * camino obliga a reaprenderlo.
 */
const ICONO_POR_MATERIA: Record<string, IconoIlustrado> = {
  // La calculadora y no la pizarra de fórmulas: a 68 px sobre la banda de la
  // tarjeta los signos pequeños de la pizarra no se leen, y la calculadora sí.
  matematicas: "calculadora",
  espanol: "edu-estanteria",
  /* `bioingenieria` (matraces con plantas) y no `edu-microscopio`, que es el
     símbolo más obvio de ciencia pero está resuelto en gris y azul marino casi
     sin color: sobre la banda verde pálida de la tarjeta se lee como un borrón
     al lado de la calculadora y la estantería, que van a todo color. El
     microscopio no se pierde — pasa a las unidades de seres vivos, donde se
     pinta a 40 px sobre blanco y ahí sí contrasta. Bold donde el fondo está
     teñido, detallado donde el fondo es blanco. */
  ciencias: "bioingenieria",
  biologia: "adn",
  ingles: "edu-pizarra-abc",
  "estudios-sociales": "edu-globo",
  "estudios": "edu-globo",
  // COMPROMISO: el set no trae bandera, capitolio ni línea de tiempo. El
  // calendario es lo más cercano a "cuándo pasó" sin fingir otra cosa.
  "historia-pr": "calendario",
  historia: "calendario",
  // Salas que no son materias escolares pero pintan tarjetas con el mismo molde.
  // `ia` cubre `ia-ninos`, `ia-jovenes` e `ia-adultos` por el prefijo. Aquí el
  // 3D gana de verdad: el set trae robot, así que se acabó el engranaje que
  // hubo que poner con el plano por no haber nada mejor.
  ia: "ia-robot",
  finanzas: "edu-pizarra-datos",
  empleo: "edu-certificado",
  universidad: "edu-libros-birrete",
  robotica: "codigo",
  programacion: "codigo",
  "ciencia-interactiva": "atomo",
  "retos-matematicos": "formula",
};

/**
 * Icono de una materia por su slug. Tolera los sufijos de nivel
 * (`-intermedia`, `-superior`, `-45`, `-k3`…) porque el contenido los usa para
 * separar paquetes de la MISMA materia.
 *
 * Devuelve `undefined` solo si no reconoce nada; quien lo llame decide si eso
 * significa "sin icono" o "cae al de la sala".
 */
export function iconoDeMateria(slug: string | undefined): IconoIlustrado | undefined {
  if (!slug) return undefined;
  if (ICONO_POR_MATERIA[slug]) return ICONO_POR_MATERIA[slug];
  const base = slug.split("-")[0];
  return ICONO_POR_MATERIA[base];
}

/* ---------------------------------------------------------------- *
 * 3. Unidad
 * ---------------------------------------------------------------- */

/*
 * Por PALABRA en el título de la unidad, no por una tabla de unidades.
 *
 * Aquí está la lección que dejó `DOMAIN_ICON` (ver config.ts): aquella mapea 47
 * claves EXACTAS contra 104 dominios distintos del contenido, así que cubre el
 * 17 % de las lecciones — y como se pinta con regla de todo-o-nada, en la
 * práctica no se ve casi nunca. Una tabla exacta contra texto que escriben
 * personas siempre pierde: el contenido dice "Numeración y Operación" y la
 * tabla decía "Numeración y Operaciones".
 *
 * Dos cambios: se busca por SUBCADENA (así "Seres vivos y estructuras" y
 * "Los seres vivos" caen las dos en el mismo sitio) y SIEMPRE hay respaldo —el
 * icono de la materia—, así que ninguna unidad se queda sin icono y no hace
 * falta la regla de todo-o-nada que apagaba la función entera.
 *
 * El orden importa: gana la primera que coincida, así que lo específico va
 * antes que lo general ("sistema solar" antes que "sistema").
 */
const PALABRAS_DE_UNIDAD: [RegExp, IconoIlustrado][] = [
  // — Ciencia —
  [/sistema solar|espacio|astro|universo|planeta|luna/i, "sistema-solar"],
  [/tierra|ambiente|clima|ecosistema|agua|recurso/i, "tierra"],
  [/ser(es)? vivo|c[eé]lula|cuerpo|planta|animal|organismo|vida/i, "edu-microscopio"],
  [/necesidad|cuidado|salud|aliment|nutrici|higiene/i, "edu-libros-manzana"],
  [/materia|qu[ií]mic|sustancia|mezcla|estado/i, "atomo"],
  [/fuerza|luz|sonido|energ[ií]a|movimiento|calor|electric/i, "edu-lampara"],
  [/explorar|cient[ií]fic|investiga|observa|indaga|experiment/i, "investigacion"],

  // — Matemáticas —
  [/geometr|figura|medici[oó]n|medida|per[ií]metro|[aá]rea/i, "escuadra"],
  // Los datos salen ANTES que los números: "Análisis de Datos y Probabilidad"
  // —50 lecciones— contiene "dato" y también caería en la calculadora.
  [/dato|probabilidad|estad[ií]stic|gr[aá]fic/i, "edu-pizarra-datos"],
  [/n[uú]mero|numeraci|operaci|suma|resta|multiplic|divisi|c[aá]lculo|fracci|decimal|[aá]lgebra|patr[oó]n/i, "calculadora"],

  // — Lengua —
  [/escritura|escribir|redacci|producci[oó]n de texto|writing/i, "edu-escritura"],
  [/lectura|leer|comprensi[oó]n|texto|literat|reading/i, "edu-libro-idea"],
  [/gram[aá]tica|lengua|ortograf|vocabul|grammar|language/i, "edu-pizarra-abc"],
  [/oral|escucha|comunica|conversa|speaking|listening|expresi[oó]n/i, "chat"],

  // — Estudios sociales e historia —
  [/geograf|lugar|mapa|mundo|global|regi[oó]n/i, "edu-globo"],
  [/gobierno|c[ií]vic|democra|ciudadan|derecho|ley|constituci/i, "edu-certificado"],
  [/histori|pasado|ta[ií]no|coloniz|siglo|continuidad|[eé]poca/i, "calendario"],
  [/cultura|identidad|arte|m[uú]sica|tradici/i, "edu-paleta"],

  // — Tecnología —
  [/tecnolog|ingenier|robot|program|c[oó]digo|algoritmo|computa/i, "codigo"],

  // — Cierres de unidad —
  [/proyecto|evaluaci|reto|examen/i, "edu-trofeo"],
];

/*
 * Último recurso, y existe por un motivo concreto.
 *
 * "Nunca se queda sin icono" era falso mientras el respaldo fuera la materia:
 * un módulo que no estuviera en la tabla, con una unidad legacy titulada "Otras
 * lecciones disponibles" —que no casa con ninguna expresión— se quedaba con el
 * hueco. Y un hueco entre cinco unidades ilustradas es exactamente el aspecto
 * roto que este módulo vino a quitar.
 *
 * La estantería es deliberadamente neutra: dice "aquí hay lecciones" sin
 * fingir que sabe de qué van.
 */
const ICONO_DE_RESPALDO: IconoIlustrado = "edu-estanteria";

/**
 * Icono de una unidad: primero por lo que dice su título, luego el de la
 * materia y, si tampoco, el de respaldo. NUNCA devuelve vacío — de eso depende
 * que la lista no salga a medias, que es como falló `DOMAIN_ICON`.
 */
export function iconoDeUnidad(titulo: string, slugMateria?: string): IconoIlustrado {
  for (const [patron, icono] of PALABRAS_DE_UNIDAD) {
    if (patron.test(titulo)) return icono;
  }
  return iconoDeMateria(slugMateria) ?? ICONO_DE_RESPALDO;
}

/* ---------------------------------------------------------------- *
 * 4. El verbo del título
 * ---------------------------------------------------------------- */

/*
 * 1.492 de las 2.409 lecciones traen el verbo pegado al título —"Comprende:
 * Observar, preguntar y registrar" y, justo debajo, "Aplica: Observar,
 * preguntar y registrar"—, así que cada tema aparece DOS VECES con el mismo
 * nombre y hay que leer hasta los dos puntos para saber en qué se diferencian.
 *
 * Se resuelve AL PINTAR y no tocando los 2.409 JSON. Migrar el contenido por un
 * asunto de presentación es cambiar 1.492 títulos que además viajan en el
 * progreso guardado y en los enlaces `?leccion=`; y las 914 lecciones sin
 * prefijo quedarían con dos convenciones conviviendo. Aquí basta con partir la
 * cadena, y el contenido nuevo que siga la convención entra solo.
 */
const VERBOS: Record<string, string> = {
  comprende: "Aprende",
  understand: "Aprende",
  aplica: "Practica",
  apply: "Practica",
  "diagnóstico": "Diagnóstico",
  diagnostico: "Diagnóstico",
  diagnostic: "Diagnóstico",
};

export interface TituloPartido {
  /** El tema, ya sin el verbo delante. */
  titulo: string;
  /** El verbo, listo para pintar como marca. `undefined` si no traía. */
  marca?: string;
}

export function partirTitulo(titulo: string): TituloPartido {
  const corte = titulo.indexOf(":");
  if (corte < 1) return { titulo };
  const verbo = VERBOS[titulo.slice(0, corte).trim().toLowerCase()];
  if (!verbo) return { titulo };
  const resto = titulo.slice(corte + 1).trim();
  // Un título que es SOLO el verbo se queda como está: partirlo deja la fila vacía.
  return resto ? { titulo: resto, marca: verbo } : { titulo };
}
